import React, { useState, useEffect, FormEvent, useCallback } from 'react';
import axiosInstance from 'config/axiosInstance';
import { fetchSearchPage } from 'reusable/RemoteSearch/remotePage';
import { SearchSelectOption } from 'reusable/RemoteSearch/SearchSelect';
import Search from 'reusable/search';
import SortBy from 'reusable/sortby';
import Pagination from 'reusable/pagination';
import Export from 'reusable/export';
import { CsvCondition } from 'config/csv';
import DeviceTable from './DeviceTable';
import AddButton from 'reusable/addButton';
import AddDialog from 'reusable/Dialog/addDialog';
import EditDialog from 'reusable/Dialog/editDialog';
import DeleteDialog from 'reusable/Dialog/deleteDialog';
import {
  DialogConfig,
  DialogField,
  DialogType,
  NewDataField,
  sortingOptions,
} from 'reusable/Dialog/dialogInterface';
import {
  addDeviceFields,
  deleteDeviceFields,
  editDeviceFields,
} from './DeviceField';
import { useTranslation } from 'react-i18next';
import ErrorPopup from 'reusable/ErrorPopup';
import { classifiedErrorMessages } from 'config/apiErrors';
import { isUnresolvedCreate, submitIdempotentCreate } from 'config/idempotentCreate';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';
import { id } from 'date-fns/locale';
import { pageAfterRemoval, pageToRecover } from 'reusable/pageRecovery';
import { PageSizeSelect, usePageSize } from 'reusable/PageSize';
import { deviceRecordExport } from 'reusable/DeleteExport';

const DeviceManagement: React.FC = () => {
  const { t } = useTranslation(['device', 'common']);
  // 保存・削除に失敗したときの説明。以前はコンソールに出すだけだった。
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const showSaveError = (error: unknown) =>
    setErrorMessages(
      classifiedErrorMessages(error, {
        network: t('ui.error-network', { ns: 'common' }),
        forbidden: t('ui.error-forbidden', { ns: 'common' }),
        server: t('ui.error-server', { ns: 'common' }),
        fallback: t('ui.save-failed', { ns: 'common' }),
      })
    );
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // EV-493。1ページの件数（20・50・100、初めは 50。localStorage に覚える）。以前は 10件で固定。
  const [rowsPerPage, setRowsPerPage] = usePageSize('deviceList.pageSize');
  const [deviceData, setDeviceData] = useState<any[]>([]);
  // EV-142。検索中・失敗時にも、出力する行と実際の取得条件を一致させる。
  const [exportConditions, setExportConditions] = useState<CsvCondition[]>([['page', 1]]);
  const [count, setCount] = useState(1);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<DialogConfig>({
    title: '',
    subtitle: '',
    fields: [],
  });
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>('');
  // UAT A15。削除の確認で保存する端末の情報（記録は端末に結びつかないため、端末の1行）。
  const [deviceToDelete, setDeviceToDelete] = useState<any>(null);
  // ADM-15。取得の失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const fetchProfilePicture = async (userId: string) => {
    try {
      const response = await axiosInstance.get(`/v1/drivers/${userId}/`);
      return response.data.profile_picture;
    } catch (error) {
      console.error(
        `Failed to fetch profile picture for user ${userId}`,
        error
      );
      return null;
    }
  };

  const fetchDeviceData = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      let apiUrl = `/v1/devices/?order_by=${sortValue}&page=${currentPage}&page_size=${rowsPerPage}&search=${encodeURIComponent(searchValue)}`;

      const response = await axiosInstance.get(apiUrl);
      const fetchedDeviceData = response.data.results;
      if (!isCurrent()) return;
      // EV-460。今のページが無くなっていたら（空のページ・件数あり）、最後の有効なページを読む。
      const recover = pageToRecover(currentPage, rowsPerPage, {
        count: response.data.count,
        rows: fetchedDeviceData.length,
      });
      if (recover !== null) {
        setCurrentPage(recover);
        return;
      }

      const updatedDeviceData = await Promise.all(
        fetchedDeviceData.map(async (device: any) => {
          if (device.user_info?.id) {
            const profilePicture = await fetchProfilePicture(
              device.user_info.id
            );
            return {
              ...device,
              user_info: {
                ...device.user_info,
                profile_picture: profilePicture,
              },
            };
          }
          return device;
        })
      );

      if (!isCurrent()) return;
      setDeviceData(updatedDeviceData);
      setExportConditions([['search', searchValue], ['sort', sortValue], ['page', currentPage]]);
      setCount(response.data.count);
      setFetchError(null);
    } catch (error) {
      if (!isCurrent()) return;
      // EV-460。ページの外（404）なら、エラーにせず1ページ目を読む。
      const recover = pageToRecover(currentPage, rowsPerPage, { error });
      if (recover !== null) {
        setCurrentPage(recover);
        return;
      }
      console.error('Failed to fetch data:', error);
      setFetchError(error);
    }
  }, [searchValue, sortValue, currentPage, rowsPerPage, beginRequest]);

  useEffect(() => {
    fetchDeviceData();
  }, [fetchDeviceData, currentDeviceId]);

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (size: number) => {
    setRowsPerPage(size);
    setCurrentPage(1);
  };

  const openDialog = (
    title: string,
    subtitle: string,
    fields: DialogField[],
    dialogType: DialogType
  ) => {
    setDialogProps({ title, subtitle, fields });
    if (dialogType === DialogType.Add) setIsAddDialogOpen(true);
    if (dialogType === DialogType.Edit) setIsEditDialogOpen(true);
    if (dialogType === DialogType.Delete) setIsDeleteDialogOpen(true);
  };

  // 開いた詳細の要求。閉じた後・開き直した後に届いた応答は捨てる。
  const beginDialogRequest = useLatestRequest();

  const closeDialog = () => {
    beginDialogRequest();
    setIsAddDialogOpen(false);
    setIsEditDialogOpen(false);
    setIsDeleteDialogOpen(false);
    setCurrentDeviceId(null);
  };

  /**
   * 「状態」の欄（固定の3つと、割当先の運転者）。
   * EV-493。以前は運転者を全件（最大5,000件・100件ずつ50回）読んで <select> に並べていた。固定の3つを先に出し、
   * 運転者は名前で検索して20件ずつ読む（SearchSelect）。読めなかったときの表示（EV-322 の既存の文言・失敗の
   * 種類・再読み込み）は欄が出す。値は以前と同じ（'available'・'in_repair'・'damaged'・'using:<運転者>'）。
   */
  const statusSearch = (knownLabels: Record<string, string> = {}) => ({
    fixedOptions: [
      { value: 'available', label: t('status.available') },
      { value: 'in_repair', label: t('status.in_repair') },
      { value: 'damaged', label: t('status.damaged') },
    ],
    fetchPage: async (query: string, page: number) => {
      const result = await fetchSearchPage<{ id: number; name: string }>('/v1/drivers/', query, page, {
        order_by: 'name',
      });
      return {
        ...result,
        items: result.items.map(
          (driver): SearchSelectOption => ({ value: `using:${driver.id}`, label: driver.name })
        ),
      };
    },
    knownLabels,
  });

  /** 「状態」の欄を検索の欄にする。 */
  const withStatusSearch = (fields: DialogField[], knownLabels?: Record<string, string>): DialogField[] =>
    fields.map((field) =>
      field.name === 'status' ? { ...field, options: undefined, search: statusSearch(knownLabels) } : field
    );

  const handleDialog = async (dialogType: DialogType, id?: string) => {
    const isCurrent = beginDialogRequest();

    if (dialogType === DialogType.Add) {
      openDialog(t('add-device.title'), '', withStatusSearch(addDeviceFields(t, {}, [])), DialogType.Add);
    } else if (
      (dialogType === DialogType.Edit || dialogType === DialogType.Delete) &&
      id
    ) {
      try {
        const deviceResponse = await axiosInstance.get(`/v1/devices/${id}/`);
        if (!isCurrent()) return;
        const deviceData = deviceResponse.data;
        setCurrentDeviceId(id);
        if (dialogType === DialogType.Edit) {
          // 割当先の運転者の名前は、候補を読む前から出す（EV-493）。
          const knownLabels: Record<string, string> = {};
          if (deviceData.status === 'using') {
            deviceData.status = `using:${deviceData.user}`;
            if (deviceData.user_info) knownLabels[deviceData.status] = deviceData.user_info.name ?? '';
          }
          openDialog(
            t('edit-device.title'),
            '',
            withStatusSearch(editDeviceFields(t, deviceData, []), knownLabels),
            DialogType.Edit
          );
        } else if (dialogType === DialogType.Delete) {
          setDeviceToDelete(deviceData);
          openDialog(
            t('delete-device.title'),
            t('delete-device.subtitle'),
            deleteDeviceFields(t, deviceData),
            DialogType.Delete
          );
        }
      } catch (error) {
        console.error('Failed to fetch device data', error);
        // ADM-15。開けなかった理由を出す（以前は押しても何も起きないように見えた）。
        showSaveError(error);
      }
    }
  };

  const handleAddDevice = async (
    e: FormEvent<Element>,
    newData: NewDataField
  ) => {
    e.preventDefault();

    let updatedStatus = newData.status;
    let driverId = null;

    if (
      typeof newData.status === 'string' &&
      newData.status.includes('using')
    ) {
      [updatedStatus, driverId] = newData.status.split(':');
    }

    const payload = { ...newData, status: updatedStatus, user: driverId };

    // EV-130（ADM-14 の AC1）。作成の操作ごとに X-Request-ID を作り、結果が分からない失敗では
    // 同じ値・同じ本文で送り直す。結果が分からなかった後に入力を変えずに押したら前回と同じ値
    // （機器IDが空でも二重に作らず、機器IDがあっても「すでにある」ではなく前回の結果が返る）。
    // 24時間の期限・前回の値を残す条件・タブに残す・利用者の印は配送先の作成と共通
    // （config/idempotentCreate.ts）。送信中の入力・閉じるの停止は AddDialog。
    const outcome = await submitIdempotentCreate('device', '/v1/devices/', payload);
    if (outcome.status === 'created') {
      setIsAddDialogOpen(false);
      setDeviceData((prevData) => [...prevData, outcome.response.data]);
    } else if (outcome.status === 'expired') {
      // RV-127。期限が近く、前回の値は使えない。一覧を読み直して警告する（次の押下は新しい値）。
      await fetchDeviceData();
      setErrorMessages([t('add-device.create-retry-expired')]);
    } else if (outcome.status === 'unknown') {
      // 作成できたかを確かめられなかった（値は残した）。ダイアログと入力を残し、一覧を読み直す。
      console.error('Failed to add device:', outcome.error);
      await fetchDeviceData();
      setErrorMessages([t('add-device.create-outcome-unknown')]);
    } else {
      console.error('Failed to add device:', outcome.error);
      // ADM-15。ダイアログを開いたまま（入力を残したまま）理由を前面に出す。
      showSaveError(outcome.error);
    }
    // EV-461。結果が分からないまま（前回の X-Request-ID を残した）なら、AddDialog が入力を送った値のまま固定する。
    return { unresolved: isUnresolvedCreate(outcome) };
  };

  const handleEditDevice = async (
    e: FormEvent<Element>,
    updatedData: NewDataField & { id: string }
  ) => {
    e.preventDefault();

    try {
      if (updatedData && updatedData.status !== null) {
        const statusValue = String(updatedData.status);

        const [updatedStatus, driverId] = statusValue.includes(':')
          ? statusValue.split(':')
          : [statusValue, null];

        updatedData.status = updatedStatus;
        updatedData.user = driverId;

        const response = await axiosInstance.patch(
          `/v1/devices/${updatedData.id}/`,
          updatedData
        );

        setDeviceData((prevData) =>
          prevData.map((device) =>
            device.id === updatedData.id ? response.data : device
          )
        );
      }

      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update device:', error);
      // ADM-15。ダイアログを開いたまま（入力を残したまま）理由を前面に出す。
      showSaveError(error);
    }
  };

  const handleDeleteDevice = async (id: string) => {
    try {
      await axiosInstance.delete(`/v1/devices/${id}/`);
      setDeviceData(deviceData.filter((device) => device.id !== id));
      setIsDeleteDialogOpen(false);
      // EV-460。画面の中で行を除くだけにせず、件数とページを読み直す。今のページの最後の1件なら1つ前のページ。
      const next = pageAfterRemoval(currentPage, deviceData.length);
      if (next !== currentPage) setCurrentPage(next);
      else await fetchDeviceData();
    } catch (error) {
      console.error('Failed to delete device:', error);
      showSaveError(error);
    }
  };

  return (
    <div>
      {/* EV-309。1024px 未満では、ツールバーの右の組を次の行へ折り返し、右に寄せる。1024px 以上は1行のまま（検索の欄が縮む）。
          右の組も、1024px 未満で収まらないときは中で折り返す。1024px 以上は1段のまま、右の組は縮めず（lg:shrink-0）、検索の欄
          （min-w-0）が縮む（#248 の独立照合の指摘）。 */}
      <div className="flex flex-row max-lg:flex-wrap gap-y-2 justify-between">
        <div className="flex w-2/5 min-w-0 justify-start">
          <Search
            placeholder={t('table.search')}
            onSearchChange={handleSearchChange}
          />
        </div>
        <div className="ml-auto flex flex-row max-lg:flex-wrap lg:shrink-0 justify-end gap-y-2 space-x-2 items-center">
          <SortBy
            options={sortingOptions(t)}
            defaultValue=""
            onChange={handleSortChange}
          />
          <AddButton
            label={t('add-device.title')}
            onClick={() => handleDialog(DialogType.Add)}
          />
          <AddDialog
            isOpen={isAddDialogOpen}
            onClose={closeDialog}
            handleSubmit={handleAddDevice}
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('add-device.save')}
            cancelLabel={t('add-device.cancel')}
            fields={dialogProps.fields}
          />
          <EditDialog
            isOpen={isEditDialogOpen}
            onClose={closeDialog}
            handleSubmit={(e: any, updatedData: any) =>
              handleEditDevice(e, { ...updatedData, id: currentDeviceId ?? '' })
            }
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('edit-device.save')}
            cancelLabel={t('edit-device.cancel')}
            fields={dialogProps.fields}
            deviceId={currentDeviceId ?? ''}
          />
          <DeleteDialog
            isOpen={isDeleteDialogOpen}
            onClose={closeDialog}
            handleSubmit={(e: any, data: any) =>
              handleDeleteDevice(data.id || '')
            }
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('delete-device.save')}
            cancelLabel={t('delete-device.cancel')}
            fields={dialogProps.fields}
            deviceId={currentDeviceId ?? ''}
            exportRecords={deviceToDelete ? deviceRecordExport(deviceToDelete) : undefined}
          />
          <Export
            data={deviceData}
            name="devices"
            conditions={exportConditions}
            exportFields={[
              { header: 'Device ID', field: 'id' },
              { header: 'Device Name', field: 'name' },
              { header: 'Device Status', field: 'status' },
              { header: 'Assigned User Name', field: 'user_info.name' },
              { header: 'Assigned User Contact', field: 'user_info.email' },
              { header: 'Assigned Date', field: 'assigned_date' },
              // UAT A08。画面の「利用開始日」にそろえた（サーバーの項目名 purchase_date は変えない）。
              { header: 'Start Date', field: 'purchase_date' },
              { header: 'Warranty Date', field: 'warranty_date' },
            ]}
          />
        </div>
      </div>

      {fetchError ? (
        <FetchFailed error={fetchError} onRetry={fetchDeviceData} />
      ) : (
        <DeviceTable
          devices={deviceData}
          onEdit={handleDialog.bind(null, DialogType.Edit)}
          onDelete={handleDialog.bind(null, DialogType.Delete)}
        />
      )}

      <div className="space-y-2">
        <PageSizeSelect id="device-page-size" value={rowsPerPage} onChange={handlePageSizeChange} />
        <Pagination
          allRows={count}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
      {errorMessages.length > 0 && (
        <ErrorPopup
          title={t('ui.error', { ns: 'common' })}
          messages={errorMessages}
          onClose={() => setErrorMessages([])}
        />
      )}
    </div>
  );
};

export default DeviceManagement;
